import { useMemo } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import PeerDelta from './PeerDelta.js';

const MATERIAL_COLOR = {
  COPPER: '#e5780b',
  PVC: '#3874ff',
  POLIETILEN: '#10b981',
  PE: '#10b981',
  BLR: '#64748b',
  STEEL: '#64748b',
  ALUMINIUM: '#a855f7'
};

const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Inner Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Outer Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

const fmtKm = m =>
  m == null ? '—' : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;

const columns = [
  {
    accessorKey: 'operator',
    header: 'Operator',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold text-900">{original.operator}</span>
    ),
    meta: { headerProps: { style: { width: '13%' } }, cellProps: { className: 'py-2' } }
  },
  {
    accessorKey: 'material',
    header: 'Material',
    cell: ({ row: { original } }) => (
      <div className="d-flex align-items-center gap-2">
        <Badge
          className="rounded-pill fs-10"
          style={{ backgroundColor: MATERIAL_COLOR[original.material] || '#6b7280' }}
        >
          {original.material}
        </Badge>
        {original.grade ? (
          <span className="fs-10 text-700">{original.grade}</span>
        ) : null}
      </div>
    ),
    meta: { headerProps: { style: { width: '14%' } } }
  },
  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => <span>{original.productLabel || '—'}</span>,
    meta: { headerProps: { style: { width: '15%' } } }
  },
  {
    accessorKey: 'phase',
    header: 'Phase',
    cell: ({ row: { original } }) => (
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <span className="text-700">{PHASE_LABEL[original.phase] || original.phase || '—'}</span>
        {original.tandem ? (
          <Badge bg="info" variant="phoenix" className="fs-10">
            tandem
          </Badge>
        ) : null}
      </div>
    ),
    meta: { headerProps: { style: { width: '13%' } } }
  },
  {
    accessorKey: 'meters',
    header: 'Produced',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column text-end">
        <span className="fw-semibold">{fmtKm(original.meters)}</span>
        <span className="fs-10 text-700">
          {original.tokens} {original.tokens === 1 ? 'drum' : 'drums'}
        </span>
      </div>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'kg',
    header: 'Used',
    cell: ({ row: { original } }) => (
      <span className="text-700">{Number(original.kg || 0).toFixed(1)} kg</span>
    ),
    meta: {
      headerProps: { style: { width: '9%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'kgPerKm',
    header: 'kg / km',
    cell: ({ row: { original } }) => (
      <span className="fw-bold text-primary">
        {original.kgPerKm != null ? original.kgPerKm.toFixed(1) : '—'}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '9%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    id: 'peer',
    accessorFn: r => r.deltaPct ?? 9999,
    header: 'vs peers',
    cell: ({ row: { original } }) => (
      <PeerDelta
        delta={original.deltaPct}
        peers={original.peerOperators}
        peerValue={
          original.peerMedianKgPerKm != null ? original.peerMedianKgPerKm.toFixed(1) : null
        }
        unit=" kg/km"
        // Leaner is cheaper, but under-running the wall fails the spec — the
        // signal worth reading is the spread, not the direction.
        higherIsBetter={false}
      />
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  }
];

/**
 * Material per operator, compared as kg per kilometre on the same product.
 *
 * Total kilograms only say who produced more. With the geometry fixed by the
 * standard, the variation in kg/km is wall thickness — running rich or lean
 * against the same spec — and that is the part an operator controls.
 */
const MaterialMatrixTable = ({ rows = [], isLoading }) => {
  const data = useMemo(() => rows, [rows]);

  const table = useAdvanceTable({
    data,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 gap-2">
        <Spinner animation="border" size="sm" variant="primary" />
        <span className="text-700 fs-9">Loading…</span>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="text-center py-5 text-700 fs-9">
        No material consumption recorded for these filters yet.
      </div>
    );
  }

  return (
    <AdvanceTableProvider {...table}>
      <AdvanceTable tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }} />
      <AdvanceTableFooter pagination className="py-3" />
    </AdvanceTableProvider>
  );
};

export default MaterialMatrixTable;
